import { Link, useNavigate, useLocation } from "react-router-dom";
import { Facebook, Instagram, MessageCircle, MapPin, Phone, Clock } from "lucide-react";
import { CLINIC, whatsappLink, telLink } from "@/lib/site";
import { useLanguage } from "@/context/LanguageContext";

export const Footer = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const go = (href) => {
    if (location.pathname !== "/") {
      navigate("/" + href);
      return;
    }
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  const footerLinks = [
    { label: t.nav.home, href: "#hero" },
    { label: t.nav.about, href: "#about" },
    { label: t.nav.services, href: "#services" },
    { label: t.nav.contact, href: "#contact" },
  ];

  return (
    <footer data-testid="footer" className="bg-foreground text-primary-foreground border-t border-primary-foreground/12">
      <div className="container-x py-16 grid gap-12 md:grid-cols-12">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <img
              src={CLINIC.logo}
              alt="FizioFit Center Logo"
              className="h-10 w-auto object-contain"
            />
            <span className="text-xl font-extrabold tracking-tight">FizioFit Center</span>
          </div>
          <p className="mt-4 text-primary-foreground/80 max-w-sm leading-relaxed text-base font-medium">
            {t.footer.tagline}
          </p>
          <div className="mt-6 flex items-center gap-3">
            <a href={CLINIC.facebook} target="_blank" rel="noopener noreferrer" data-testid="footer-facebook" className="h-11 w-11 grid place-items-center rounded-sm border border-primary-foreground/20 hover:bg-accent hover:border-accent transition-colors"><Facebook size={18} /></a>
            <a href={CLINIC.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="h-11 w-11 grid place-items-center rounded-sm border border-primary-foreground/20 hover:bg-accent hover:border-accent transition-colors"><Instagram size={18} /></a>
            <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="h-11 w-11 grid place-items-center rounded-sm border border-primary-foreground/20 hover:bg-accent hover:border-accent transition-colors"><MessageCircle size={18} /></a>
            <a href={CLINIC.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-maps" className="h-11 w-11 grid place-items-center rounded-sm border border-primary-foreground/20 hover:bg-accent hover:border-accent transition-colors"><MapPin size={18} /></a>
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-extrabold">{t.footer.menu}</div>
          <ul className="mt-5 space-y-3">
            {footerLinks.map((l) => (
              <li key={l.href}>
                <button onClick={() => go(l.href)} data-testid={`footer-link-${l.href.slice(1)}`} className="text-base font-bold text-primary-foreground/80 hover:text-accent transition-colors cursor-pointer">{l.label}</button>
              </li>
            ))}
            <li>
              <Link to="/about" className="text-base font-bold text-primary-foreground/80 hover:text-accent transition-colors">
                {t.aboutPage?.title || "Rreth Nesh & Filozofia"}
              </Link>
            </li>
            <li>
              <Link to="/privacy-policy" data-testid="footer-privacy-policy" className="text-base font-bold text-primary-foreground/80 hover:text-accent transition-colors">
                {t.footer.privacy}
              </Link>
            </li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-extrabold">{t.footer.contact}</div>
          <ul className="mt-5 space-y-4 text-primary-foreground/90 text-base font-medium">
            <li className="flex items-start gap-3"><Phone size={18} className="text-accent mt-0.5" /><a href={telLink} className="font-bold hover:text-accent transition-colors">{CLINIC.phone}</a></li>
            <li className="flex items-start gap-3"><MapPin size={18} className="text-accent mt-0.5" /><span>{CLINIC.address}</span></li>
            <li className="flex items-start gap-3"><Clock size={18} className="text-accent mt-0.5" /><span>{t.contact.hoursVal}</span></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-primary-foreground/12">
        <div className="container-x py-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-primary-foreground/40">
          <span>© {new Date().getFullYear()} FizioFit Center. {t.footer.rights}</span>
          <span>Lushnjë, Albania</span>
        </div>
      </div>
    </footer>
  );
};
